import type { HardnessLevel } from '~api/services';

type HardnessColourConfig = {
  /** Hardness level */
  level: HardnessLevel;
  /** Display label for the hardness level */
  label: string;
  /** Fill color for the hardness zone */
  fill: string;
  /** Stroke color for the hardness zone */
  stroke: string;
  /** Opacity for the hardness zone */
  opacity: number;
};

/**
 * Colour configuration for hardness zones
 * Note: Tailwind theme variables are used for colours so hardness zones follow the active theme.
 */
const HARDNESS_COLOURS: Record<HardnessLevel, HardnessColourConfig> = {
  UNKNOWN: {
    level: 'UNKNOWN',
    label: 'Unknown',
    fill: 'var(--color-muted)',
    stroke: 'var(--color-border)',
    opacity: 0.3,
  },
  AIR: {
    level: 'AIR',
    label: 'Air',
    fill: 'var(--color-info)',
    stroke: 'var(--color-info)',
    opacity: 0.3,
  },
  SOFT: {
    level: 'SOFT',
    label: 'Soft',
    fill: 'var(--color-success)',
    stroke: 'var(--color-success)',
    opacity: 0.5,
  },
  MEDIUM: {
    level: 'MEDIUM',
    label: 'Medium',
    fill: 'var(--color-warning)',
    stroke: 'var(--color-warning)',
    opacity: 0.5,
  },
  HARD: {
    level: 'HARD',
    label: 'Hard',
    fill: 'var(--color-destructive)',
    stroke: 'var(--color-destructive)',
    opacity: 0.5,
  },
  EXTRAHARD: {
    level: 'EXTRAHARD',
    label: 'Extra Hard',
    fill: 'var(--color-primary)',
    stroke: 'var(--color-primary)',
    opacity: 0.5,
  },
} as const;

/**
 * Get color configuration for a hardness level (with fallback)
 * @param level - Hardness level
 * @returns Colour configuration for the hardness level
 */
const getHardnessColour = (level: string): HardnessColourConfig => {
  return (
    HARDNESS_COLOURS[level.replaceAll(' ', '').toUpperCase() as HardnessLevel] ?? {
      level: level as HardnessLevel,
      label: level,
      fill: 'var(--color-muted)',
      stroke: 'var(--color-border)',
      opacity: 0.3,
    }
  );
};

export { getHardnessColour, HARDNESS_COLOURS, type HardnessColourConfig };
